// @givmo/app-admin — Steps Competition ENTRIES panel (EP-WEB10).
//
// The admin entrant view: `GET …/{id}/entries` returns TRUE identities (unlike the consumer
// leaderboard, which honours per-entry visibility) plus a per-entry FLAGGED-DAY count — the
// integrity signal an admin acts on. From here an entry can be disqualified or reinstated; both
// mutations are idempotent server-side, so a double click is harmless.
//
// Disqualify is confirm-gated (it removes someone from a competition with money on the line);
// reinstate is not (it restores the default state and undoes the destructive action).
//
// Rows are selected by the `competitions-entry-row-` prefix. The table deliberately uses NO row
// disclosure — the disclosure toggle's default testid is `${rowTestId}-expand`, which a prefix
// locator counts as a row, so a screen with a prefix-selected row family either passes `expandTestId`
// or has no toggle at all. Everything an admin needs is a column, so there is nothing to disclose.

import { useMemo, useState } from 'react';
import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { QueryFunctionContext } from '@tanstack/react-query';
import {
  stepsCompetitionsAdminDisqualifyAdminStepsCompetitionEntryMutation,
  stepsCompetitionsAdminGetAdminStepsCompetitionQueryKey,
  stepsCompetitionsAdminReinstateAdminStepsCompetitionEntryMutation,
} from '@givmo/api-client/query';
import { sdk, type AdminEntryOut } from '@givmo/api-client';
import {
  Badge,
  Button,
  Card,
  CardHeader,
  DataTable,
  EmptyState,
  Icon,
  testIdProp,
  toast,
  type DataTableColumn,
} from '@givmo/design-system';
import { Users } from 'lucide-react';
import { ConfirmDialog } from '../../components/confirm-dialog';
import {
  canDisqualify,
  canReinstate,
  entrantName,
  entryCharityName,
  entryStatusView,
  errorMessage,
  extractEntries,
  flaggedDayCount,
  formatSteps,
} from './competitions-decode';

const PREFIX = 'competitions-entries';
const PAGE_LIMIT = 50;

function entriesKey(competitionId: string) {
  return ['steps_competitions_admin-list_admin_steps_competition_entries', competitionId] as const;
}

async function fetchEntriesPage(
  competitionId: string,
  offset: number,
  signal: AbortSignal,
): Promise<AdminEntryOut[]> {
  const { data } = await sdk.stepsCompetitionsAdminListAdminStepsCompetitionEntries({
    path: { steps_competition_id: competitionId },
    query: { 'page[limit]': PAGE_LIMIT, 'page[offset]': offset },
    signal,
    throwOnError: true,
  });
  return extractEntries(data);
}

export interface CompetitionEntriesPanelProps {
  competitionId: string;
  /** Manage actions are hidden once the competition is finalized or canceled — nothing left to act on. */
  actionable: boolean;
}

export function CompetitionEntriesPanel({
  competitionId,
  actionable,
}: CompetitionEntriesPanelProps) {
  const queryClient = useQueryClient();
  const [pendingDisqualify, setPendingDisqualify] = useState<AdminEntryOut | null>(null);
  const [actingEntryId, setActingEntryId] = useState<string | null>(null);

  const key = entriesKey(competitionId);
  const list = useInfiniteQuery({
    queryKey: key,
    queryFn: ({ pageParam, signal }: QueryFunctionContext<typeof key, number>) =>
      fetchEntriesPage(competitionId, pageParam, signal),
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) =>
      lastPage.length < PAGE_LIMIT ? undefined : allPages.reduce((n, p) => n + p.length, 0),
  });

  const entries = useMemo(() => (list.data ? list.data.pages.flat() : []), [list.data]);

  // Disqualify/reinstate changes the ACTIVE-entry count, which the DETAIL read projects as
  // `participant_count` and the page renders as the "N entrants" chip. Invalidating only this list
  // would refresh the rows while leaving that chip stale next to them.
  const refresh = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: key }),
      queryClient.invalidateQueries({
        queryKey: stepsCompetitionsAdminGetAdminStepsCompetitionQueryKey({
          path: { steps_competition_id: competitionId },
        }),
      }),
    ]);
  };

  const disqualify = useMutation({
    ...stepsCompetitionsAdminDisqualifyAdminStepsCompetitionEntryMutation(),
    onSuccess: async () => {
      setPendingDisqualify(null);
      setActingEntryId(null);
      await refresh();
      toast.success('Entrant disqualified.');
    },
    onError: (error) => {
      setActingEntryId(null);
      toast.error(errorMessage(error, 'We couldn’t disqualify this entrant. Please try again.'));
    },
  });

  const reinstate = useMutation({
    ...stepsCompetitionsAdminReinstateAdminStepsCompetitionEntryMutation(),
    onSuccess: async () => {
      setActingEntryId(null);
      await refresh();
      toast.success('Entrant reinstated.');
    },
    onError: (error) => {
      setActingEntryId(null);
      toast.error(errorMessage(error, 'We couldn’t reinstate this entrant. Please try again.'));
    },
  });

  const columns: DataTableColumn<AdminEntryOut>[] = useMemo(
    () => [
      {
        id: 'entrant',
        header: 'Entrant',
        width: '30%',
        truncate: true,
        cell: (entry) => (
          <span className="truncate font-w600 text-text-strong">{entrantName(entry)}</span>
        ),
      },
      {
        id: 'charity',
        header: 'Charity',
        width: '24%',
        truncate: true,
        cell: (entry) => (
          <span className="truncate text-text-secondary">{entryCharityName(entry)}</span>
        ),
      },
      {
        id: 'steps',
        header: 'Steps',
        width: '110px',
        align: 'right',
        cell: (entry) => (
          <span className="tabular-nums text-text-strong">{formatSteps(entry.total_steps)}</span>
        ),
      },
      {
        id: 'flagged',
        header: 'Flagged days',
        width: '130px',
        align: 'right',
        cell: (entry) => {
          const flagged = flaggedDayCount(entry);
          return flagged === 0 ? (
            <span className="tabular-nums text-text-tertiary">—</span>
          ) : (
            <Badge tone="warning" testId={`${PREFIX}-flagged-${entry.id}`}>
              {flagged}
            </Badge>
          );
        },
      },
      {
        id: 'status',
        header: 'Status',
        width: '140px',
        cell: (entry) => {
          const status = entryStatusView(entry.status);
          return (
            <Badge tone={status.tone} dot testId={`${PREFIX}-status-${entry.id}`}>
              {status.label}
            </Badge>
          );
        },
      },
      // Spread in, never pushed onto the array afterwards: a memoized array that is mutated
      // after the fact grows one more actions column on every render.
      ...(actionable
        ? [
            {
              id: 'actions',
              header: '',
              width: '140px',
              align: 'right',
              // Each action is gated on the exact status it can act on — never on the other's negation. A
              // WITHDRAWN entrant (and any status we don't recognize) gets neither: the entrant already left,
              // so a Disqualify button there could only ever 409.
              cell: (entry) => {
                if (canReinstate(entry)) {
                  return (
                    <Button
                      appearance="saas"
                      tone="secondary"
                      size="sm"
                      loading={actingEntryId === entry.id && reinstate.isPending}
                      onClick={() => {
                        setActingEntryId(entry.id);
                        reinstate.mutate({
                          path: { steps_competition_id: competitionId, entry_id: entry.id },
                        });
                      }}
                      testId={`${PREFIX}-reinstate-${entry.id}`}
                    >
                      Reinstate
                    </Button>
                  );
                }
                if (canDisqualify(entry)) {
                  return (
                    <Button
                      appearance="saas"
                      tone="secondary"
                      size="sm"
                      onClick={() => setPendingDisqualify(entry)}
                      testId={`${PREFIX}-disqualify-${entry.id}`}
                    >
                      Disqualify
                    </Button>
                  );
                }
                return null;
              },
            } as DataTableColumn<AdminEntryOut>,
          ]
        : []),
    ],
    [actionable, actingEntryId, competitionId, reinstate],
  );

  return (
    <Card {...testIdProp(PREFIX)}>
      <CardHeader
        title="Entrants"
        subtitle="Flagged days mark step counts that looked implausible. Disqualifying removes an entrant from the results."
      />
      <DataTable
        columns={columns}
        rows={entries}
        rowKey={(entry) => entry.id}
        rowTestId={(entry) => `${PREFIX}-row-${entry.id}`}
        loading={list.isPending}
        loadingTestId={`${PREFIX}-loading`}
        error={list.isError ? errorMessage(list.error, 'Failed to load entrants.') : undefined}
        onRetry={() => void list.refetch()}
        errorTestId={`${PREFIX}-error`}
        retryTestId={`${PREFIX}-retry`}
        emptyState={
          <EmptyState
            testId={`${PREFIX}-empty-state`}
            icon={<Icon icon={Users} size={20} aria-hidden />}
            title="No entrants yet"
            description="Employees who join this competition will appear here."
          />
        }
        footer={
          list.hasNextPage === true ? (
            <>
              <span className="font-sans text-s13 tracking-normal text-text-tertiary tabular-nums">
                Showing {entries.length} entrants
              </span>
              <Button
                appearance="saas"
                tone="secondary"
                size="sm"
                disabled={list.isFetchingNextPage}
                onClick={() => void list.fetchNextPage()}
                testId={`${PREFIX}-load-more`}
              >
                {list.isFetchingNextPage ? 'Loading…' : 'Load more'}
              </Button>
            </>
          ) : undefined
        }
      />

      <ConfirmDialog
        open={pendingDisqualify !== null}
        onOpenChange={(next) => {
          if (!next) setPendingDisqualify(null);
        }}
        title="Disqualify this entrant?"
        subject={pendingDisqualify !== null ? entrantName(pendingDisqualify) : undefined}
        body="They will be removed from the results and cannot win the prize. You can reinstate them later."
        confirmLabel="Disqualify"
        destructive
        loading={disqualify.isPending}
        onConfirm={() => {
          if (pendingDisqualify === null) return;
          setActingEntryId(pendingDisqualify.id);
          disqualify.mutate({
            path: { steps_competition_id: competitionId, entry_id: pendingDisqualify.id },
          });
        }}
        testId={`${PREFIX}-disqualify-confirm`}
      />
    </Card>
  );
}
